import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
import { Goal } from '../Models/CarePlans/Goal.model';
import { convertObjectToArray, copyObject } from '../utils/common';
import { Plan } from '../Models/CarePlans/Plan.model';
import { Role } from '../Models/Role/Role.model';
import { ProviderPreview, SchedulePlan } from '../Models/CarePlans/SchedulePlan.model';
import { Barrier } from '../Models/CarePlans/Barrier.model';
import { Strength } from '../Models/CarePlans/Strength.model';
import { Metric } from '../Models/CarePlans/Metric.model';
import { NextStep } from '../Models/CarePlans/NextStep.model';
import { Intervention } from '../Models/CarePlans/Intervention.model';
import { MessageAlert } from '../Models/Shared/MessageAlert.model';
import { CarePlanEnum } from '../Enum/CarePlanEnum';
import { CarePlanSignatureTypeEnum } from '../Enum/CarePlanSignatureTypeEnum';
import { CarePlanSignatureRequest } from '../Models/CarePlans/CarePlanSignatureRequest';
import { CarePlanDualSignatureRequest } from '../Models/CarePlans/CarePlanDualSignatureRequest';
import { ReviewHistory } from '../Models/CarePlans/ReviewHistory.model';
import { isValid, isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toDate } from '../utils/dateHelpers';
import { IntegratedCarePlan, Participants } from '../Models/CarePlans/IntegratedCarePlan.model';

import { getNewProgressModalState } from '../components/IntegratedCarePlan/CarePlan/utils';
import { ItemType } from '../components/IntegratedCarePlan/CarePlan/types';
import { setIsLoading, setShowMessage } from './shared.slice';
import { TimeIntervalTypeEnum } from '../Enum/TimeIntervalTypeEnum';
import { CreatedBy } from '../Models/Shared/CreatedBy.model';
import { DocumentEngagement } from '../Models/DocumentEngagements/DocumentEngagement.model';
import { CarePlanSignatureStatus } from '../Models/CarePlans/CarePlanSignatureStatus.model';

const carePlanUpdate = createSlice({
    name: 'carePlanUpdate',
    initialState: {
        carePlanSignatureStatus: new CarePlanSignatureStatus(),
        draggedItem: null,
        editDialogOpen: false,
        editDueDate: null as Date | null,
        editItem: null,
        editLabel: '',
        editStatus: CarePlanEnum.InProgress,
        editTimeIntervalType: TimeIntervalTypeEnum.Day,
        editTimeIntervalInDays: 0,
        goalStatusTab: CarePlanEnum.InProgress,
        editType: null as ItemType | null,
        integratedCarePlan: new IntegratedCarePlan(),
        progressModal: getNewProgressModalState(),
        reviewSignChecked: {} as Record<string, boolean>,
        reasonsForDelete: [],
        roles: [] as Role[],
        signatureType: CarePlanSignatureTypeEnum.Provider,
        interventions: [] as Intervention[],
        memberInterventions: [] as Intervention[],
        memberSignature: '',
        memberSignatureRequest: null as ReviewHistory | null,
        newInterventionName: '',
        providerPreview: null as ProviderPreview | null,
        providerSignature: '',
        providerSignatureRequest: null as ReviewHistory | null,
        showPlanEditMode: false,
        showInterventionEditMode: false,
        showInterventionDeleteAlert: false,
        showSignatureModal: false,
        isFetching: false,
        isFetchingInterventions: false,
        isSavingIntervention: false,
        isUpdatingIntervention: false,
        isSavingReviewHistory: false,
        error: new MessageAlert(),
        verbalConsentName: '',
    },
    reducers: {
        _setCarePlanSignatureStatus(state, action) {
            state.carePlanSignatureStatus = action.payload;
        },
        _setDraggedItem(state, action) {
            state.draggedItem = action.payload;
        },
        _setEditDialogOpen(state, action) {
            state.editDialogOpen = action.payload;
        },
        _setEditDueDate(state, action) {
            state.editDueDate = action.payload;
        },
        _setEditItem(state, action) {
            state.editItem = action.payload;
        },
        _setEditLabel(state, action) {
            state.editLabel = action.payload;
        },
        _setEditStatus(state, action) {
            state.editStatus = action.payload;
        },
        _setEditTimeIntervalInDays(state, action) {
            state.editTimeIntervalInDays = action.payload;
        },
        _setEditTimeIntervalType(state, action) {
            state.editTimeIntervalType = action.payload;
        },
        _setGoalStatusTab(state, action) {
            state.goalStatusTab = action.payload;
        },
        _setEditType(state, action) {
            state.editType = action.payload;
        },
        _setIntegratedCarePlan(state, action) {
            state.integratedCarePlan = action.payload;
        },

        _setMemberSignature(state, action) {
            state.memberSignature = action.payload;
        },
        _setMemberSignatureRequest(state, action) {
            state.memberSignatureRequest = action.payload;
        },
        _setProgressModal(state, action) {
            state.progressModal = {
                ...state.progressModal,
                ...action.payload,
            };
        },
        _setProviderPreview(state, action) {
            state.providerPreview = action.payload;
        },
        _setProviderSignature(state, action) {
            state.providerSignature = action.payload;
        },
        _setProviderSignatureRequest(state, action) {
            state.providerSignatureRequest = action.payload;
        },
        _setReviewSignChecked(state, action) {
            state.reviewSignChecked = action.payload;
        },
        _setSignatureType(state, action) {
            state.signatureType = action.payload;
        },
        _setReasonsForDelete(state, action) {
            state.reasonsForDelete = action.payload;
        },
        _setRoles(state, action) {
            state.roles = action.payload;
        },
        _setMemberInterventions(state, action) {
            state.memberInterventions = action.payload;
        },
        _setInterventions(state, action) {
            state.interventions = action.payload;
        },
        _setNewInterventionName(state, action) {
            state.newInterventionName = action.payload;
        },
        _setShowSignatureModal(state, action) {
            state.showSignatureModal = action.payload;
        },
        _toggleShowPlanEditMode(state, action) {
            state.showPlanEditMode = action.payload;
        },
        _toggleShowInterventionEditMode(state, action) {
            state.showInterventionEditMode = action.payload;
        },
        _toggleShowInterventionDeleteAlert(state, action) {
            state.showInterventionDeleteAlert = action.payload;
        },
        _toggleIsFetching(state, action) {
            state.isFetching = action.payload;
        },
        _toggleIsFetchingInterventions(state, action) {
            state.isFetchingInterventions = action.payload;
        },
        _toggleIsSavingIntervention(state, action) {
            state.isSavingIntervention = action.payload;
        },
        _toggleIsUpdatingIntervention(state, action) {
            state.isUpdatingIntervention = action.payload;
        },
        _toggleIsSavingReviewHistory(state, action) {
            state.isSavingReviewHistory = action.payload;
        },
        _setError(state, action) {
            state.error = action.payload;
        },
        _setVerbalConsentName(state, action) {
            state.verbalConsentName = action.payload;
        },
    },
});

const {
    _setCarePlanSignatureStatus,
    _setDraggedItem,
    _setEditDialogOpen,
    _setEditDueDate,
    _setEditItem,
    _setEditLabel,
    _setEditStatus,
    _setEditType,
    _setEditTimeIntervalInDays,
    _setEditTimeIntervalType,
    _setGoalStatusTab,
    _setIntegratedCarePlan,
    _setProgressModal,
    _setProviderPreview,
    _setProviderSignatureRequest,
    _setReviewSignChecked,
    _setSignatureType,
    _setReasonsForDelete,
    _setRoles,
    _setInterventions,
    _setMemberInterventions,
    _setMemberSignature,
    _setMemberSignatureRequest,
    _setNewInterventionName,
    _setProviderSignature,
    _setShowSignatureModal,
    _toggleShowPlanEditMode,
    _toggleShowInterventionEditMode,
    _toggleShowInterventionDeleteAlert,
    _toggleIsFetching,
    _toggleIsFetchingInterventions,
    _toggleIsSavingIntervention,
    _toggleIsUpdatingIntervention,
    _toggleIsSavingReviewHistory,
    _setError,
    _setVerbalConsentName,
} = carePlanUpdate.actions;

export const getCarePlanByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const response = await axios.get(`${API_URL}/careplan/getcareplanbymemberid`, { params: { memberId } });
        response.data = prepareGoalsAndPlans(response.data, getState().documentEngagement?.engagementDraft);
        dispatch(_setIntegratedCarePlan(response.data));
        dispatch(setIsLoading(false));
    };

export const getCarePlanSignatureStatus =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/careplan/getcareplansignaturestatuses`, { params: { memberId } });
            dispatch(_setCarePlanSignatureStatus(response.data));
        } catch (error) {
            dispatch(setShowMessage(true, 'Failed to fetch care plan signature status', 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

const prepareGoalsAndPlans = (integratedCarePlan: IntegratedCarePlan, engagementDraft: DocumentEngagement) => {
    [
        ...(integratedCarePlan.Goals || []),
        ...(integratedCarePlan.SchedulePlans || []),
        ...(integratedCarePlan.Plans || []),
        ...(integratedCarePlan.Barriers || []),
        ...(integratedCarePlan.Strengths || []),
        ...(integratedCarePlan.Metrics || []),
        ...(integratedCarePlan.NextSteps || []),
    ].forEach((plan) => {
        plan.IsSelected = engagementDraft.SummaryNotes?.some((s) => s.Id === plan.Id);
    });

    return integratedCarePlan;
};

export const setDraggedItem =
    (item: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setDraggedItem(item));
    };

export const setEditDialogOpen =
    (isOpen: boolean): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditDialogOpen(isOpen));
    };

export const setEditDueDate =
    (dueDate: Date | null): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditDueDate(dueDate));
    };

export const setEditItem =
    (item: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditItem(item));
    };

export const setEditLabel =
    (label: string): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditLabel(label));
    };

export const setEditStatus =
    (status: CarePlanEnum): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditStatus(status));
    };

export const setEditType =
    (type: ItemType | null): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditType(type));
    };

export const setEditTimeIntervalInDays =
    (timeIntervalInDays: number): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditTimeIntervalInDays(timeIntervalInDays));
    };

export const setEditTimeIntervalType =
    (timeIntervalType: TimeIntervalTypeEnum): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEditTimeIntervalType(timeIntervalType));
    };

export const setGoalStatusTab =
    (goalStatusTab: string): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setGoalStatusTab(goalStatusTab));
    };

export const setIntegratedCarePlan =
    (integratedCarePlan: IntegratedCarePlan): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setIntegratedCarePlan(integratedCarePlan));
    };

export const setMemberSignatureRequest =
    (memberSignatureRequest: ReviewHistory): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setMemberSignatureRequest(memberSignatureRequest));
    };

export const setProgressModal =
    (progressModal: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setProgressModal(progressModal));
    };

export const setProviderPreview =
    (providerPreview: ProviderPreview): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setProviderPreview(providerPreview));
    };

export const setProviderSignatureRequest =
    (providerSignatureRequest: ReviewHistory): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setProviderSignatureRequest(providerSignatureRequest));
    };

export const setReviewSignChecked =
    (goalId: string, checked: boolean): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setReviewSignChecked({ ...getState().carePlanUpdate.reviewSignChecked, [goalId]: checked }));
    };

export const setSignatureType =
    (signatureType: CarePlanSignatureTypeEnum): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setSignatureType(signatureType));
    };

export const handleEdit =
    (item: any, type: ItemType): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setEditItem(item));
        dispatch(setEditType(type));
        if (type === 'goal') {
            dispatch(setEditLabel(item.Name || ''));
            dispatch(setEditStatus(item.Status));
            dispatch(setEditDueDate(item.DueDate ? new Date(item.DueDate) : null));
        } else if (type === 'plan') {
            dispatch(setEditLabel(item.Name || ''));
        } else if (type === 'schedulePlan') {
            dispatch(setProviderPreview(item.Provider || null));
            dispatch(setEditTimeIntervalType(item.TimeIntervalType));
            dispatch(setEditTimeIntervalInDays(item.TimeIntervalIndays));
        } else {
            dispatch(setEditLabel(item.label || item.Name || ''));
        }
        dispatch(setEditDialogOpen(true));
    };

export const updateLastReviewedBy =
    (carePlanSignatureRequest: CarePlanSignatureRequest, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSavingReviewHistory(true));
        await axios
            .put(`${API_URL}/careplan/updatelastreviewedby`, null, { params: carePlanSignatureRequest })
            .then((response) => {
                dispatch(setIntegratedCarePlan(response.data));
                if (callBack) {
                    callBack(response.data);
                }
            })
            .finally(() => {
                dispatch(_toggleIsSavingReviewHistory(false));
            });
    };

export const addGoal =
    (memberId: string, name: string, dueDate: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addgoal`, { name, dueDate }, { params: { memberId } });

        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, Goals: [copy, ...integratedCarePlan.Goals] }));
        dispatch(setEditDialogOpen(false));
        dispatch(setIsLoading(false));
    };

export const editGoals =
    (memberId: string, goals: Goal[], updatedBy: CreatedBy): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editgoals`, goals, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.IsReviewed = false;
                carePlanData.Goals = carePlanData.Goals.map((goal) => {
                    const editedGoal = goals.find((g) => g.Id === goal.Id);
                    let copy = null;
                    if (editedGoal !== undefined) {
                        copy = copyObject(editedGoal);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                        copy.UpdatedProviderById = updatedBy;
                    }
                    return Boolean(copy) ? copy : goal;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setShowMessage(true, 'Care plan updated successfully', 'success'));
                dispatch(setEditDialogOpen(false));
                dispatch(setIsLoading(false));
            });
    };

export const saveDragAndDrop =
    (memberId: string, goal: Goal, updatedBy: CreatedBy, callback?: () => void): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/savedraganddrop`, goal, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.IsReviewed = false;
                carePlanData.LastUpdatedTime = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                carePlanData.Goals = carePlanData.Goals.map((g) => {
                    if (g.Id === goal.Id) {
                        goal.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        goal.UpdatedProviderById = updatedBy;
                        return { ...g, ...goal };
                    }
                    return g;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
                dispatch(getCarePlanSignatureStatus(memberId));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
                if (callback) {
                    callback();
                }
            });
    };

export const addGoalProgress =
    (memberId: string, goalId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addgoalprogress`, progress, { params: { memberId, goalId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.Goals.forEach((goal) => {
                    if (goal.Id === goalId) {
                        goal.Status = goal.Status === CarePlanEnum.Open ? CarePlanEnum.InProgress : goal.Status;
                        goal.Progress = goal.Progress || [];
                        goal.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        goal.Progress.push(response.data);
                        goal.NewProgressNote = '';
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const setMemberSignature =
    (signature: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setMemberSignature(signature));
    };

export const setProviderSignature =
    (signature: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setProviderSignature(signature));
    };

export const setShowSignatureModal =
    (show: boolean): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setShowSignatureModal(show));
    };

export const getReasonForDelete = (): AppThunk => async (dispatch, getState) => {
    const response = await axios.get(`${API_URL}/member/getresonfordelete`);
    dispatch(_setReasonsForDelete(convertObjectToArray(response.data)));
};

export const handleSendToMember =
    (memberId: string, share: boolean): AppThunk =>
    async (dispatch, getState) => {
        const integratedCarePlan = getState().carePlanUpdate.integratedCarePlan;
        dispatch(setIntegratedCarePlan({ ...integratedCarePlan, ShareWithMember: share }));
        const response = await axios.put(`${API_URL}/careplan/sharewithmember?memberId=${memberId}&share=${share}`);
        if (response.data) {
            dispatch(setShowMessage(true, 'Care plan shared with member successfully', 'success'));
        } else {
            dispatch(setShowMessage(true, 'Failed to share care plan with member', 'error'));
        }
    };

export const setVerbalConsentName =
    (name: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setVerbalConsentName(name));
    };

export const updateParticipants =
    (memberId: string, participants: Participants): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/updateparticipants`, participants, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.Participants = participants;
                carePlanData.LastUpdatedTime = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const updateNarrative =
    (memberId: string, narrative: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios.put(`${API_URL}/careplan/updatenarrative?memberId=${memberId}&narrative=${narrative}`).then(() => {
            let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
            carePlanData.Narrative = narrative;
            carePlanData.LastUpdatedTime = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
            carePlanData.IsReviewed = false;
            dispatch(setIntegratedCarePlan(carePlanData));
            dispatch(setShowMessage(true, 'Care plan saved', 'success'));
            dispatch(setIsLoading(false));
        });
    };

export const updateIsReviewed =
    (memberId: string, isReviewed: boolean): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.put(`${API_URL}/careplan/updateisreviewed?memberId=${memberId}&isReviewed=${isReviewed}`);
            // Goal selections belong to the engagement and are not returned by the review endpoint.
            dispatch(
                setIntegratedCarePlan({
                    ...getState().carePlanUpdate.integratedCarePlan,
                    IsReviewed: response.data.IsReviewed,
                    IsReviewedDate: response.data.IsReviewedDate,
                    ReviewHistory: response.data.ReviewHistory,
                })
            );
            dispatch(setShowMessage(true, `Care plan ${isReviewed ? 'marked as reviewed' : 'review status removed'}`, 'success'));
        } catch (error) {
            dispatch(setShowMessage(true, 'Failed to update review status', 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const handleProgressSave =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const { progressModal } = getState().carePlanUpdate;
        const { goalId, value, type } = progressModal;
        switch (type) {
            case 'goal':
                dispatch(addGoalProgress(memberId, goalId, value));
                break;
            case 'strength':
                dispatch(addStrengthProgress(memberId, goalId, value));
                break;
            case 'barrier':
                dispatch(addBarrierProgress(memberId, goalId, value));
                break;
            case 'nextStep':
                dispatch(addNextStepProgress(memberId, goalId, value));
                break;
            case 'plan':
                dispatch(addPlanProgress(memberId, goalId, value));
                break;
            case 'metric':
                dispatch(addMetricProgress(memberId, goalId, value));
                break;
            case 'schedulePlan':
                break;
            default:
                break;
        }
        // dispatch(setShowMessage(true, 'Care plan saved', 'success'));
        dispatch(setProgressModal(getNewProgressModalState()));
    };

export const addPlan =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addplan`, { Name: name }, { params: { memberId } });
        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, Plans: [copy, ...integratedCarePlan.Plans] }));
        dispatch(setEditDialogOpen(false));
        dispatch(setIsLoading(false));
    };

export const editPlans =
    (memberId: string, plans: Plan[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editplans`, plans, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.Plans = carePlanData.Plans.map((plan) => {
                    const editedPlan = plans.find((p) => p.Id === plan.Id);
                    let copy = null;
                    if (editedPlan !== undefined) {
                        copy = copyObject(editedPlan);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                    }
                    return Boolean(copy) ? copy : plan;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setEditDialogOpen(false));
                dispatch(setIsLoading(false));
            });
    };

export const addPlanProgress =
    (memberId: string, planId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addplanprogress`, progress, { params: { memberId, planId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.Plans.forEach((plan) => {
                    if (plan.Id === planId) {
                        plan.Status = plan.Status === CarePlanEnum.Open ? CarePlanEnum.InProgress : plan.Status;
                        plan.Progress = plan.Progress || [];
                        plan.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        plan.Progress.push(response.data);
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const toggleShowPlanEditMode =
    (show: boolean): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleShowPlanEditMode(show));
    };

export const getRoles = (): AppThunk => async (dispatch, getState) => {
    const response = await axios.get(`${API_URL}/provider/getroles`);
    const roles = ['Behavioural Health Specialist - FUHFUM', 'Nurse Practitioner - FUHFUM'];
    const filteredRoles = response.data.filter((role: Role) => !roles.includes(role.Name));
    dispatch(_setRoles(filteredRoles));
};

export const addSchedulePlan =
    (memberId: string, schedulePlan: SchedulePlan): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        axios
            .put(`${API_URL}/careplan/addscheduleplan`, schedulePlan, { params: { memberId } })
            .then((response) => {
                const copy = copyObject(response.data);
                const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                copy.UpdatedAt = updatedAtUTC;
                dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, SchedulePlans: [copy, ...integratedCarePlan.SchedulePlans] }));
            })
            .catch((err) => {
                dispatch(setCarePlanError({ Show: true, Message: err?.response?.data?.error }));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
                dispatch(setEditDialogOpen(false));
            });
    };

export const editSchedulePlan =
    (memberId: string, schedulePlan: SchedulePlan): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        const existingProviderId = integratedCarePlan.SchedulePlans.find((s) => s.Id === schedulePlan.Id)?.Provider?.Id;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/editscheduleplan`, schedulePlan, { params: { memberId, existingProviderId } });
        let copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(
            _setIntegratedCarePlan({
                ...integratedCarePlan,
                SchedulePlans: [...integratedCarePlan.SchedulePlans.map((s) => (s.Id === schedulePlan.Id ? copy : s))],
            })
        );
        dispatch(setIsLoading(false));
        dispatch(setEditDialogOpen(false));
    };

// Barrier start

export const addBarrier =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addbarrier`, { Name: name }, { params: { memberId } });
        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, Barriers: [copy, ...integratedCarePlan.Barriers] }));
        dispatch(setIsLoading(false));
        dispatch(setEditDialogOpen(false));
    };

export const addBarrierProgress =
    (memberId: string, barrierId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addbarrierprogress`, progress, { params: { memberId, barrierId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.Barriers.forEach((barrier) => {
                    if (barrier.Id === barrierId) {
                        barrier.Progress = barrier.Progress || [];
                        barrier.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        barrier.Progress.push(response.data);
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const editBarriers =
    (memberId: string, barriers: Barrier[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editbarriers`, barriers, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.Barriers = carePlanData.Barriers.map((barrier) => {
                    const editedBarrier = barriers.find((b) => b.Id === barrier.Id);
                    let copy = null;
                    if (editedBarrier !== undefined) {
                        copy = copyObject(editedBarrier);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                    }
                    return Boolean(copy) ? copy : barrier;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setEditDialogOpen(false));
                dispatch(setIsLoading(false));
            });
    };

// Barrier End

// Strength start

export const addStrength =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addstrength`, { Name: name }, { params: { memberId } });
        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, Strengths: [copy, ...integratedCarePlan.Strengths] }));
        dispatch(setIsLoading(false));
        dispatch(setEditDialogOpen(false));
    };

export const addStrengthProgress =
    (memberId: string, strengthId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addstrengthprogress`, progress, { params: { memberId, strengthId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.Strengths.forEach((strength) => {
                    if (strength.Id === strengthId) {
                        strength.Status = strength.Status === CarePlanEnum.Open ? CarePlanEnum.InProgress : strength.Status;
                        strength.Progress = strength.Progress || [];
                        strength.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        strength.Progress.push(response.data);
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const editStrengths =
    (memberId: string, strengths: Strength[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editstrengths`, strengths, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.Strengths = carePlanData.Strengths.map((strength) => {
                    const editedStrength = strengths.find((s) => s.Id === strength.Id);
                    let copy = null;
                    if (editedStrength !== undefined) {
                        copy = copyObject(editedStrength);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                    }
                    return Boolean(copy) ? copy : strength;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
                dispatch(setEditDialogOpen(false));
            });
    };

// Strength End

// Metric start

export const addMetric =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addmetric`, { Name: name }, { params: { memberId } });
        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, Metrics: [copy, ...integratedCarePlan.Metrics] }));
        dispatch(setIsLoading(false));
        dispatch(setEditDialogOpen(false));
    };

export const editMetrics =
    (memberId: string, metrics: Metric[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editmetrics`, metrics, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.Metrics = carePlanData.Metrics.map((metric) => {
                    const editedMetric = metrics.find((m) => m.Id === metric.Id);
                    let copy = null;
                    if (editedMetric !== undefined) {
                        copy = copyObject(editedMetric);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                    }
                    return Boolean(copy) ? copy : metric;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setEditDialogOpen(false));
                dispatch(setIsLoading(false));
            });
    };

// Add metric progress
export const addMetricProgress =
    (memberId: string, metricId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addmetricprogress`, progress, { params: { memberId, metricId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.Metrics.forEach((metric) => {
                    if (metric.Id === metricId) {
                        metric.Status = metric.Status === CarePlanEnum.Open ? CarePlanEnum.InProgress : metric.Status;
                        metric.Progress = metric.Progress || [];
                        metric.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        metric.Progress.push(response.data);
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

// Metric End

// Next Step start

export const addNextStep =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { integratedCarePlan } = getState().carePlanUpdate;
        dispatch(setIsLoading(true));
        const response = await axios.put(`${API_URL}/careplan/addnextstep`, { Name: name }, { params: { memberId } });
        const copy = copyObject(response.data);
        const updatedAtUTC = formatInTimeZone(toDate(copy.UpdatedAt), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
        copy.UpdatedAt = updatedAtUTC;
        dispatch(_setIntegratedCarePlan({ ...integratedCarePlan, NextSteps: [copy, ...integratedCarePlan.NextSteps] }));
        dispatch(setIsLoading(false));
        dispatch(setEditDialogOpen(false));
    };

// Add step next progress
export const addNextStepProgress =
    (memberId: string, nextStepId: string, progressNote: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        const progress = {
            Name: progressNote,
            CreatedAt: new Date().toISOString(),
        };
        axios
            .put(`${API_URL}/careplan/addnextstepprogress`, progress, { params: { memberId, nextStepId } })
            .then((response) => {
                let data = copyObject(getState().carePlanUpdate.integratedCarePlan);
                data.NextSteps.forEach((nextStep) => {
                    if (nextStep.Id === nextStepId) {
                        nextStep.Status = nextStep.Status === CarePlanEnum.Open ? CarePlanEnum.InProgress : nextStep.Status;
                        nextStep.Progress = nextStep.Progress || [];
                        nextStep.UpdatedAt = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        nextStep.Progress.push(response.data);
                    }
                });
                dispatch(setIntegratedCarePlan(data));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
            });
    };

export const editNextSteps =
    (memberId: string, nextSteps: NextStep[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        await axios
            .put(`${API_URL}/careplan/editnextSteps`, nextSteps, { params: { memberId } })
            .then(() => {
                let carePlanData = copyObject(getState().carePlanUpdate.integratedCarePlan) as IntegratedCarePlan;
                carePlanData.NextSteps = carePlanData.NextSteps.map((step) => {
                    const editedStep = nextSteps.find((m) => m.Id === step.Id);
                    let copy = null;
                    if (editedStep !== undefined) {
                        copy = copyObject(editedStep);
                        const now = formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
                        copy.UpdatedAt = now;
                    }
                    return Boolean(copy) ? copy : step;
                });
                dispatch(setIntegratedCarePlan(carePlanData));
            })
            .finally(() => {
                dispatch(setIsLoading(false));
                dispatch(setEditDialogOpen(false));
            });
    };

// Next Step End

// Interventions start

export const getInterventionsByMember =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/careplan/getinterventionsbymember`, { params: { memberId } });
        const engagementDraft = getState().documentEngagement?.engagementDraft;
        (response.data || []).forEach((intervention) => {
            intervention.IsSelected = engagementDraft.SummaryNotes?.some((s) => s.Id === intervention.Id);
        });
        dispatch(_setMemberInterventions(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const getInterventionsByKeyword =
    (keyword: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetchingInterventions(true));
        dispatch(_setNewInterventionName(keyword));
        const response = await axios.get(`${API_URL}/careplan/getinterventionsbykeyword`, { params: { keyword } });
        dispatch(_setInterventions(response.data));
        dispatch(_toggleIsFetchingInterventions(false));
    };

export const addIntervention =
    (memberId: string, name: string): AppThunk =>
    async (dispatch, getState) => {
        const { memberInterventions } = getState().carePlanUpdate;
        dispatch(_toggleIsSavingIntervention(true));
        const response = await axios.put(`${API_URL}/careplan/addintervention`, { MemberId: memberId, Name: name });
        dispatch(_setMemberInterventions([...memberInterventions, response.data]));
        dispatch(_toggleIsSavingIntervention(false));
        dispatch(_setNewInterventionName(''));
    };

export const updateInterventions =
    (interventions: Intervention[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsUpdatingIntervention(true));
        await axios.put(`${API_URL}/careplan/updateInterventions`, interventions);
        dispatch(_toggleIsUpdatingIntervention(false));
        dispatch(_toggleShowInterventionEditMode(false));
    };

export const removeIntervention =
    (interventionId: string): AppThunk =>
    async (dispatch, getState) => {
        const { memberInterventions } = getState().carePlanUpdate;
        dispatch(_toggleIsSavingIntervention(true));
        await axios.delete(`${API_URL}/careplan/deleteIntervention`, { params: { interventionId } });
        dispatch(_setMemberInterventions(memberInterventions.filter((m) => m.Id !== interventionId)));
        dispatch(_toggleIsSavingIntervention(false));
    };

export const setMemberInterventions =
    (interventions: Intervention[]): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setMemberInterventions(interventions));
    };

export const toggleShowInterventionEditMode =
    (show: boolean): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleShowInterventionEditMode(show));
    };

export const toggleShowInterventionDeleteAlert =
    (show: boolean): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleShowInterventionDeleteAlert(show));
    };

export const setNewInterventionName =
    (note: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setNewInterventionName(note));
    };

export const setCarePlanError =
    (error: MessageAlert): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setError(error));
    };

export const resetCarePlanError = (): AppThunk => (dispatch, getState) => {
    dispatch(_setError(new MessageAlert()));
};

export const hasBeenReviewedRecently =
    (planEntry: string, reviewDetail: ReviewHistory): AppThunk =>
    (dispatch, getState) => {
        planEntry = planEntry || null;
        const lastReviewed = reviewDetail?.LastReviewedOn || null;
        const signature = reviewDetail?.Signature || null;

        let status = false;

        if (planEntry !== null && lastReviewed !== null && signature !== null) {
            status = isAfter(toDate(reviewDetail?.LastReviewedOn), toDate(planEntry));
        }
        return status;
    };

export const saveCarePlanSignatures =
    (
        providerSignature: string | null,
        memberSignature: { signature: string; signatureType: CarePlanSignatureTypeEnum } | null,
        goalId: string[],
        callback?: () => void
    ): AppThunk =>
    async (dispatch, getState) => {
        if (!providerSignature && !memberSignature) {
            return;
        }

        const integratedCarePlan = getState().carePlanUpdate.integratedCarePlan;
        dispatch(setIsLoading(true));

        const signaturePayload = new CarePlanDualSignatureRequest();

        if (providerSignature) {
            signaturePayload.ProviderSignature = {
                CarePlanId: integratedCarePlan?.Id,
                GoalId: goalId,
                Label: CarePlanSignatureTypeEnum.Provider,
                Signature: providerSignature,
            } as CarePlanSignatureRequest;
        }

        if (memberSignature) {
            signaturePayload.MemberSignature = {
                CarePlanId: integratedCarePlan?.Id,
                GoalId: goalId,
                Label: memberSignature.signatureType,
                Signature: memberSignature.signature,
            } as CarePlanSignatureRequest;
        }

        const response = await axios.put(`${API_URL}/careplan/savecareplansignatures`, signaturePayload);
        dispatch(setIntegratedCarePlan(response.data));

        if (providerSignature) {
            const reviewHistory = response.data?.ReviewHistory?.[0];
            dispatch(setProviderSignatureRequest(reviewHistory));
        }

        if (memberSignature) {
            const reviewHistory = response.data?.ReviewHistoryMember?.[0];
            dispatch(setMemberSignatureRequest(reviewHistory));
        }

        dispatch(setIsLoading(false));

        if (callback) {
            callback();
        }
    };

export default carePlanUpdate.reducer;
